import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, Disc3, Library, Loader2, Music2, Shuffle, Upload } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { CineCombo } from '../CineCombo';
import { AssetPicker } from '../assets/AssetPicker';
import { absUrl, errMsg, makeMtvCover, musicFromAsset, mtvGenres, mtvLyrics, patchProject, pickMtvVersion, uploadMusic } from '../../../lib/aiStudioApi';
import { cn } from '../../../lib/utils';
import { SongTimeline } from './SongTimeline';

const box = 'rounded-xl border border-neutral-800 bg-neutral-900/40 p-5';
const Label = ({ children }) => <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">{children}</div>;
const VOCALS = [['keep', 'Original'], ['f', 'Female'], ['m', 'Male']];

/** Step 1 · the song. A new song is listened to automatically: timed lyrics, length, beats and sections. */
export function SongCard({ project, onChanged }) {
  const ref = useRef(null);
  const player = useRef(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [time, setTime] = useState(0);
  const m = project.music || {};
  const mtv = project.mtv || {};
  const listen = () => mtvLyrics(project.id).then(onChanged).catch((e) => toast.error(errMsg(e)));
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await onChanged(); listen(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const measured = (e) => {
    const s = Math.round(e.currentTarget.duration || 0);
    if (s && s !== project.song_seconds) patchProject(project.id, { song_seconds: s }).then(onChanged).catch(() => {});
  };
  return (
    <div className={box} data-testid="mtv-song-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-100"><Music2 size={15} className="text-amber-300" /> Your song</div>
      <p className="mb-3 text-xs text-neutral-400">Upload the song (MP3, WAV, M4A · up to 25 MB, up to 5 minutes) or reuse one from your Asset Library. It plays at full volume as the video's soundtrack; the lyrics, beat and sections are picked up automatically.</p>
      {m.url ? (
        <div className="mb-3 space-y-2" data-testid="mtv-current-song">
          <div className="text-xs text-neutral-200">{m.name || 'Your song'} {m.source === 'mtv' && <span className="ml-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-200">new version</span>}</div>
          <audio ref={player} src={absUrl(m.url)} controls preload="metadata" onLoadedMetadata={measured} onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
            className="h-8 w-full" data-testid="mtv-song-audio" />
          <SongTimeline analysis={mtv.analysis} lines={mtv.lines} scenes={project.scenes} time={time}
            onSeek={(t) => { if (player.current) { player.current.currentTime = t; setTime(t); } }} />
        </div>
      ) : <div className="mb-3 rounded-lg border border-dashed border-neutral-700 p-6 text-center text-xs text-neutral-400" data-testid="mtv-no-song">No song yet</div>}
      <input ref={ref} type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*" className="hidden" data-testid="mtv-song-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) run(() => uploadMusic(project.id, f), `"${f.name}" is the song — listening for lyrics and beats`); }} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={() => ref.current?.click()} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="mtv-upload-song-btn">
          {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Upload size={13} className="mr-1" />} {m.url ? 'Replace song' : 'Upload song'}
        </Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setPicking(true)} className="border-neutral-700 text-neutral-200" data-testid="mtv-library-song-btn"><Library size={13} className="mr-1" /> From Asset Library</Button>
      </div>
      <AssetPicker open={picking} onOpenChange={setPicking} folders={['music']} title="Pick the song for this music video"
        onPick={(a) => run(() => musicFromAsset(project.id, a.id), `"${a.name}" is the song — listening for lyrics and beats`)} />
    </div>
  );
}

function Take({ label, sub, url, active, onUse, testId }) {
  return (
    <div className={cn('space-y-1.5 rounded-lg border p-3 transition-colors duration-200', active ? 'border-amber-400 bg-amber-500/5' : 'border-neutral-800')} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0"><div className="truncate text-xs font-medium text-neutral-100">{label}</div><div className="text-[10px] text-neutral-400">{sub}</div></div>
        {active ? <span className="flex items-center gap-1 text-[11px] text-amber-200"><Check size={12} /> In the video</span>
          : <Button size="sm" variant="outline" onClick={onUse} className="h-7 border-neutral-700 text-[11px] text-neutral-200" data-testid={`${testId}-use`}>Use this</Button>}
      </div>
      <audio src={absUrl(url)} controls preload="none" className="h-7 w-full" />
    </div>
  );
}

/** Genre interpretation: same melody, new genre (2 takes). */
export function CoverCard({ project, onChanged }) {
  const [genres, setGenres] = useState([]);
  const [f, setF] = useState({ genre: '', vocal: 'keep', singer: '' });
  useEffect(() => {
    mtvGenres().then((d) => { setGenres(d.genres); if ((d.genres || []).includes(project.genre)) setF((x) => ({ ...x, genre: x.genre || project.genre })); }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const job = project.mtv_job || {};
  const running = job.status === 'running';
  const m = project.music || {};
  const original = m.source === 'mtv' ? project.mtv?.original : m;
  const start = async () => {
    if (!f.genre) { toast.error('Pick a genre'); return; }
    try { await makeMtvCover(project.id, f); toast.success('Making 2 takes — about 1–3 minutes'); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={box} data-testid="mtv-cover-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-neutral-100"><Shuffle size={15} className="text-amber-300" /> Make it a new genre <span className="font-normal text-neutral-500">(optional)</span></div>
      <p className="mb-3 text-xs text-neutral-400">Same melody, new genre — and a new singer style if you like. Makes 2 takes (≈12–25 cr, about $0.06–$0.13). Want your own voice on it? Make the cover, split its vocals, then swap the singer.</p>
      <div className="grid gap-3 md:grid-cols-3">
        <CineCombo label="Target genre" value={f.genre} options={genres} allowCustom onChange={(genre) => setF({ ...f, genre })} placeholder="Pick or type a genre" clearLabel="Clear" testId="mtv-genre" />
        <div><Label>Voice</Label>
          <div className="flex rounded-md border border-neutral-700 p-0.5">
            {VOCALS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => setF({ ...f, vocal: v })} data-testid={`mtv-vocal-${v}`}
                className={cn('flex-1 rounded px-2 py-1.5 text-[11px] transition-colors', f.vocal === v ? 'bg-amber-500 text-neutral-900' : 'text-neutral-300 hover:text-neutral-100')}>{l}</button>
            ))}
          </div>
        </div>
        <div><Label>Singer style (optional)</Label>
          <Input value={f.singer} onChange={(e) => setF({ ...f, singer: e.target.value })} placeholder="e.g. husky soulful alto, airy falsetto" className="h-9 border-neutral-700 bg-neutral-950 text-xs" data-testid="mtv-singer" /></div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] text-neutral-400" data-testid="mtv-job-status">{!/studio|Re-imagining|take/.test(job.step || '') ? '' : running ? job.step : job.status === 'failed' ? <span className="text-red-300">{job.error}</span> : job.step}</div>
        <Button size="sm" disabled={running || !original?.url} onClick={start} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="mtv-make-cover-btn">
          {running ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Shuffle size={13} className="mr-1" />} {running ? 'Working…' : `Make it ${f.genre || 'a new genre'} · ≈12–25 cr`}
        </Button>
      </div>
    </div>
  );
}

/** Final soundtrack picker: the original, a genre cover, a voice swap or a custom mix. */
export function SoundtrackPicker({ project, onChanged }) {
  const m = project.music || {};
  const mtv = project.mtv || {};
  const original = m.source === 'mtv' ? mtv.original : m;
  const versions = mtv.versions || [];
  const use = (id) => pickMtvVersion(project.id, id).then(() => { toast.success('Soundtrack updated'); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  if (!versions.length) return null;
  return (
    <div className={box} data-testid="mtv-soundtrack-picker">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-neutral-100"><Disc3 size={15} className="text-amber-300" /> Final soundtrack</div>
      <div className="grid gap-2 md:grid-cols-2" data-testid="mtv-versions">
        {original?.url && <Take label="Original song" sub="as uploaded" url={original.url} active={m.source !== 'mtv'} onUse={() => use('original')} testId="mtv-take-original" />}
        {versions.map((v) => (
          <Take key={v.id} label={v.title} sub={[v.genre, VOCALS.find(([k]) => k === v.vocal && k !== 'keep')?.[1], v.singer].filter(Boolean).join(' · ')} url={v.url}
            active={m.version_id === v.id} onUse={() => use(v.id)} testId={`mtv-take-${v.id}`} />
        ))}
      </div>
    </div>
  );
}
